import { ComponentProps, useRef, useState } from "react";
import { MultiSelect } from "@bonliva-crm/ui";
import { useLocale, useUpdateEffect } from "@hooks";
import { useFormField } from "@clientUtils";
import { FilterWithFavouriteProps } from "../utils/filters-record-types";
import { ControllableCollapsibleFavFilter } from "./_base/controllable-collapsible-fav-filter";
import { ConsultantPipelineStatus } from "@graphql";

export type PipelineStatusFilterData = string[];

export type PipelineStatusFilterProps = FilterWithFavouriteProps;

export const PipelineStatusFilter = (props: PipelineStatusFilterProps) => {
  const { locale } = useLocale();
  const title = props.label ?? locale.pipelineStatus;
  const options: LabelValue[] = [
     {
      value: ConsultantPipelineStatus.NotPlaced,
      label: locale.consultantPipelineBoardHeaders[ConsultantPipelineStatus.NotPlaced]
    },
    {
      value: ConsultantPipelineStatus.NotRelevant,
      label: locale.consultantPipelineBoardHeaders[ConsultantPipelineStatus.NotRelevant]
    },
    {
      value: ConsultantPipelineStatus.RelevantInTheFuture,
      label:
        locale.consultantPipelineBoardHeaders[
          ConsultantPipelineStatus.RelevantInTheFuture
        ]
    },
    {
      value: ConsultantPipelineStatus.Warm,
      label: locale.consultantPipelineBoardHeaders[ConsultantPipelineStatus.Warm]
    },
    {
      value: ConsultantPipelineStatus.RelevantNow,
      label: locale.consultantPipelineBoardHeaders[ConsultantPipelineStatus.RelevantNow]
    },
    {
      value: ConsultantPipelineStatus.ReadyToBePresented,
      label:
        locale.consultantPipelineBoardHeaders[ConsultantPipelineStatus.ReadyToBePresented]
    },
    {
      value: ConsultantPipelineStatus.Presented,
      label: locale.consultantPipelineBoardHeaders[ConsultantPipelineStatus.Presented]
    },
    {
      value: ConsultantPipelineStatus.Booked,
      label: locale.consultantPipelineBoardHeaders[ConsultantPipelineStatus.Booked]
    }
  ];

  const selectRef = useRef(null);
  const { field, fieldState, setFieldValue } = useFormField<PipelineStatusFilterData>(
    props.name
  );
  const [expanded, setExpanded] = useState(fieldState.isDirty);

  useUpdateEffect(() => {
    if (expanded) {
      selectRef.current.focus();
    }
  }, [expanded]);

  const commonProps: ComponentProps<typeof MultiSelect<Option>> = {
    options,
    name: props.name,
    value:
      field?.value.map((x) => ({
        label: locale.consultantPipelineBoardHeaders[x],
        value: x
      })) ?? [],
    onChange: (selected) => {
      field.onChange(selected.map((s) => s.value));
    },
    hideSelectedOptions: false,
    controlShouldRenderValue: false,
    placeholder: title,
    isSearchable: true,
    maxMenuHeight: 600,
    showBadge: field?.value !== null,
    optionType: "checkbox",
    isClearable: field?.value !== null,
    closeMenuOnSelect: false,
    hidePlaceholderOnFocus: false,
    showOptionDivider: false
  };

  if (props.setFavorite) {
    return (
      <ControllableCollapsibleFavFilter
        title={title}
        isDirty={field.value?.length > 0}
        isClearable={field.value?.length > 0}
        isExpanded={expanded}
        onClear={() => setFieldValue([])}
        favorite={props.favorite}
        onSetFavorite={props.setFavorite}
        onExpand={() => setExpanded(!expanded)}
      >
        <MultiSelect<Option>
          {...commonProps}
          selectRef={selectRef}
          isClearable={false}
          openMenuOnFocus
          menuPortalTarget={document.body}
          menuPlacement="auto"
          name={"pipelineStatus"}
          controlShouldRenderValue
          showBadge={false}
          placeholder={locale.SelectValue}
          styleProps={{
            menu: { showBorder: true },
            valueContainer: { padding: 0 },
            menuPortal: { zIndex: 9999 }
          }}
        />
      </ControllableCollapsibleFavFilter>
    );
  }

  return (
    <MultiSelect<Option>
      {...commonProps}
      styleProps={{
        control: { showBorder: true },
        menu: { showBorder: true }
      }}
    />
  );
};

type Option = { label: string; value: string | null };
